import type { Player } from '../../types/player';
import type { LeagueConfig } from '../../types/league';
import type { LeagueFormatType } from '../../components/LeagueFormatSelector';

// Import format-specific formulas
import { 
  calculateStandardValue,
  getStandardPositionPriority,
  getStandardDraftStrategy 
} from './standardFormula';

import { 
  calculateSuperflexValue,
  getSuperflexPositionPriority,
  getSuperflexDraftStrategy 
} from './superflexFormula';

import { 
  calculateTwoQbValue,
  getTwoQbPositionPriority,
  getTwoQbDraftStrategy 
} from './twoQbFormula';

import { 
  calculateBestBallValue,
  getBestBallPositionPriority,
  getBestBallDraftStrategy,
  getBestBallRosterTargets
} from './bestBallFormula';

import { 
  calculateDynastyValue,
  getDynastyPositionPriority,
  getDynastyDraftStrategy,
  getDynastyAgeTargets
} from './dynastyFormula';

import { 
  applyMarketAlignmentGuardrails,
  validateMarketAlignment,
  applyConsensusFloors
} from './marketAlignmentGuardrails';

import { buildConstrainedRankings } from './constrainedRankingEngine';
import type { PlayerVote } from './feedbackAdjustment';

/**
 * Main Formula Engine
 * Routes to appropriate format-specific formula based on league configuration
 */

export interface FormulaResult {
  rankedPlayers: Player[];
  positionPriority: string[];
  draftStrategy: string[];
  formatInsights: FormatInsights;
}

export interface FormatInsights {
  marketAlignment: string;
  keyFactors: string[];
  rosterTargets?: any;
  ageTargets?: any;
  scoringNotes?: string[];
}

export function calculateFormatSpecificRankings(
  players: Player[],
  leagueConfig: LeagueConfig,
  playerFeedback?: Record<string, PlayerVote[]>
): FormulaResult {
  const format = leagueConfig.format.type as LeagueFormatType;
  
  // Use constrained ranking engine for better market alignment
  if (format === 'superflex' || format === '2qb' || format === 'standard' || format === 'bestball' || format === 'dynasty') {
    console.log(`🎯 FORMULA ENGINE: Using constrained ranking engine for ${format}...`);
    console.log(`🎯 FORMULA ENGINE: Input players count: ${players.length}`);
    
    const constrainedResult = buildConstrainedRankings(players, format, playerFeedback);
    
    console.log(`🎯 FORMULA ENGINE: Constrained result:`, {
      playersCount: constrainedResult.rankedPlayers.length,
      positionCounts: constrainedResult.positionCounts,
      alignmentPct: constrainedResult.alignmentPct
    });
    
    // Get format-specific recommendations  
    const positionPriority = getPositionPriority(format);
    const draftStrategy = getDraftStrategy(format);
    const formatInsights = getConstrainedFormatInsights(format, constrainedResult);
    
    return {
      rankedPlayers: constrainedResult.rankedPlayers,
      positionPriority,
      draftStrategy,
      formatInsights
    };
  }
  
  // Fall back to original formula system for other formats
  const processedPlayers = players.map(player => {
    let value: number;
    
    switch (format) {
      case 'bestball':
        value = calculateBestBallValue(player, leagueConfig);
        break;
      case 'dynasty':
        value = calculateDynastyValue(player, leagueConfig);
        break;
      default:
        value = calculateStandardValue(player, leagueConfig);
        break;
    }
    
    return {
      ...player,
      value,
      formatAdjustedValue: value
    };
  });
  
  // Sort by value and assign ranks
  const rankedPlayers = processedPlayers
    .sort((a, b) => (b.value || 0) - (a.value || 0))
    .map((player, index) => ({
      ...player,
      rank: index + 1
    }));
  
  // Get format-specific recommendations  
  const positionPriority = getPositionPriority(format);
  const draftStrategy = getDraftStrategy(format);
  const formatInsights = getFormatInsights(format, rankedPlayers);
  
  return {
    rankedPlayers,
    positionPriority,
    draftStrategy,
    formatInsights
  };
}

function getPositionPriority(format: LeagueFormatType): string[] {
  switch (format) {
    case 'superflex':
      return getSuperflexPositionPriority();
    case '2qb':
      return getTwoQbPositionPriority();
    case 'bestball':
      return getBestBallPositionPriority();
    case 'dynasty':
      return getDynastyPositionPriority();
    default:
      return getStandardPositionPriority();
  }
}

function getDraftStrategy(format: LeagueFormatType): string[] {
  switch (format) {
    case 'superflex':
      return getSuperflexDraftStrategy();
    case '2qb':
      return getTwoQbDraftStrategy();
    case 'bestball':
      return getBestBallDraftStrategy();
    case 'dynasty':
      return getDynastyDraftStrategy();
    default:
      return getStandardDraftStrategy();
  }
}

function getFormatInsights(format: LeagueFormatType, rankedPlayers: Player[]): FormatInsights {
  const top20 = rankedPlayers.slice(0, 20);
  
  switch (format) {
    case 'superflex':
      const sfQBs = top20.filter(p => p.position === 'QB').length;
      const sfSkill = top20.filter(p => ['RB', 'WR', 'TE'].includes(p.position)).length;
      
      return {
        marketAlignment: sfQBs <= 10 ? 'Aligned with market (7-10 QBs in top 24)' : 'Over-weighted on QBs vs market',
        keyFactors: [
          'QB rushing ability provides highest floors',
          'Elite skill players (CMC, Chase, Lamb) still valuable',
          'Moderate QB premium, not extreme',
          'WAR-based replacement levels'
        ],
        scoringNotes: [
          `${sfQBs} QBs in top 20 (market target: 8-10)`,
          `${sfSkill} skill positions in top 20`,
          'Elite rushing QBs (Allen, Hurts, Lamar) prioritized'
        ]
      };
      
    case '2qb':
      const twoqbQBs = top20.filter(p => p.position === 'QB').length;
      const twoqbSkill = top20.filter(p => ['RB', 'WR', 'TE'].includes(p.position)).length;
      
      return {
        marketAlignment: twoqbQBs <= 14 ? 'Aligned with 2QB market (10-14 QBs in top 24)' : 'Over-weighted on QBs vs 2QB market',
        keyFactors: [
          'MUST start 2 QBs - highest QB premium format',
          'Elite rushing QBs are tier 1 assets',
          'Zero-RB strategy becomes viable',
          'Target 3rd QB for bye weeks and insurance'
        ],
        scoringNotes: [
          `${twoqbQBs} QBs in top 20 (2QB target: 10-14)`,
          `${twoqbSkill} skill positions in top 20`,
          'QB scarcity drives all draft decisions'
        ]
      };
      
    case 'bestball':
      return {
        marketAlignment: 'Optimized for weekly upside and depth value',
        keyFactors: [
          'Weekly ceiling more important than floor',
          'Rookie upside valued highly',
          'Depth pieces get significant boost',
          'Bye week coverage crucial'
        ],
        rosterTargets: getBestBallRosterTargets(),
        scoringNotes: [
          'High-variance players boosted',
          'Late-round picks gain value for depth',
          'WRs need 6-10 on roster for optimal lineups'
        ]
      };
      
    case 'dynasty':
      return {
        marketAlignment: 'Multi-year NPV with age curve emphasis',
        keyFactors: [
          'Age is most important factor (30% weight)',
          'Rookie upside heavily weighted',
          'Position longevity considered',
          'Multi-year value projection'
        ],
        ageTargets: getDynastyAgeTargets(),
        scoringNotes: [
          'Players under 25 get major boosts',
          'RBs penalized for shorter careers',
          'WRs prioritized for longevity'
        ]
      };
      
    default: // standard
      const stdQBs = top20.filter(p => p.position === 'QB').length;
      return {
        marketAlignment: 'Traditional 1-QB format prioritization',
        keyFactors: [
          'Proven production heavily weighted (25%)',
          'Consistency valued over upside',
          'No QB premium - wait on QB',
          'Elite TE advantage significant'
        ],
        scoringNotes: [
          `${stdQBs} QBs in top 20 (typical: 0-2)`,
          'RB/WR scarcity drives early rounds',
          'Streaming positions (K/DEF) deprioritized'
        ]
      };
  }
}

/**
 * Market Validation Functions
 * Compare our rankings to known market consensus
 */
export function validateAgainstMarket(
  rankedPlayers: Player[], 
  format: LeagueFormatType
): MarketValidation {
  const top24 = rankedPlayers.slice(0, 24);
  const qbCount = top24.filter(p => p.position === 'QB').length;
  const skillCount = top24.filter(p => ['RB', 'WR', 'TE'].includes(p.position)).length;
  
  // Market benchmarks by format
  const benchmarks = {
    standard: { qbRange: [0, 2], skillRange: [18, 22] },
    superflex: { qbRange: [7, 11], skillRange: [12, 16] },
    '2qb': { qbRange: [10, 14], skillRange: [10, 14] },
    bestball: { qbRange: [2, 4], skillRange: [16, 20] },
    dynasty: { qbRange: [3, 6], skillRange: [15, 19] }
  };
  
  const benchmark = benchmarks[format];
  const qbAligned = qbCount >= benchmark.qbRange[0] && qbCount <= benchmark.qbRange[1];
  const skillAligned = skillCount >= benchmark.skillRange[0] && skillCount <= benchmark.skillRange[1];
  
  return {
    qbCount,
    skillCount,
    qbAligned,
    skillAligned,
    overallScore: qbAligned && skillAligned ? 'Aligned' : 'Misaligned',
    recommendations: generateAlignmentRecommendations(format, qbCount, skillCount, benchmark)
  };
}

interface MarketValidation {
  qbCount: number;
  skillCount: number;
  qbAligned: boolean;
  skillAligned: boolean;
  overallScore: string;
  recommendations: string[];
}

function generateAlignmentRecommendations(
  format: LeagueFormatType,
  qbCount: number, 
  skillCount: number,
  benchmark: { qbRange: number[], skillRange: number[] }
): string[] {
  const recommendations: string[] = [];
  
  if (qbCount < benchmark.qbRange[0]) {
    recommendations.push(`Increase QB weighting - only ${qbCount} QBs in top 24, target ${benchmark.qbRange[0]}-${benchmark.qbRange[1]}`);
  }
  
  if (qbCount > benchmark.qbRange[1]) {
    recommendations.push(`Reduce QB weighting - ${qbCount} QBs in top 24, target ${benchmark.qbRange[0]}-${benchmark.qbRange[1]}`);
  }
  
  if (skillCount < benchmark.skillRange[0]) {
    recommendations.push(`Boost skill position values - only ${skillCount} in top 24`);
  }
  
  if (recommendations.length === 0) {
    recommendations.push(`✅ Rankings aligned with ${format} market consensus`);
  }
  
  return recommendations;
}

function getConstrainedFormatInsights(format: LeagueFormatType, constrainedResult: any): FormatInsights {
  const { positionCounts, target, alignmentPct } = constrainedResult;
  
  const formatInsights: FormatInsights = {
    marketAlignment: `${Math.round(alignmentPct)}% Market Aligned`,
    keyFactors: [
      'Constrained ranking engine enforces market distribution',
      'Role confidence penalties applied to backup players',
      'WAR-based scoring with position scarcity adjustments',
      'Dynamic bias toward underrepresented positions'
    ],
    scoringNotes: []
  };
  
  // Add specific notes for each position
  for (const [pos, count] of Object.entries(positionCounts)) {
    const targetCount = target[pos] || 0;
    if (targetCount > 0) {
      const diff = count - targetCount;
      if (diff === 0) {
        formatInsights.scoringNotes!.push(`${pos}: ${count} (perfect)`);
      } else if (diff > 0) {
        formatInsights.scoringNotes!.push(`${pos}: ${count} (+${diff} over target)`);
      } else {
        formatInsights.scoringNotes!.push(`${pos}: ${count} (${diff} under target)`);
      }
    }
  }
  
  return formatInsights;
}